import { Button } from '@ctw/shared/components/button';
import { withErrorBoundary } from '@ctw/shared/components/errors/error-boundary';
import { SelectInput, type SelectOption } from '@ctw/shared/components/form/select-input';
import { ProgressBar } from '@ctw/shared/components/indicators/progress-bar';
import { notify } from '@ctw/shared/components/toast';
import {
  type PatientFact,
  PatientFactExtractionGroupedTable,
  type PatientFactGroup,
} from '@ctw/shared/content/fact-extraction/patient-fact-extraction-grouped-table';
import { tw } from '@ctw/shared/utils/tailwind';
import { faDownload } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type ReactNode, useState } from 'react';

/**
 * Note: FOR DEMO DATA ONLY
 */
const FAKE_FIELDS_EXTRACTED = 57;
/**
 * Note: FOR DEMO DATA ONLY
 */
const FAKE_FIELDS_TOTAL = 72;
/**
 * Note: FOR DEMO DATA ONLY
 */
const FAKE_SPECIMENS: Array<SelectOption> = [
  {
    label: 'SP-2026-05102 • Apr 1, 2026',
    value: 'SP-2026-05102',
  },
  {
    label: 'SP-2026-04890 • Mar 17, 2026',
    value: 'SP-2026-04890',
  },
  {
    label: 'SP-2026-04521 • Mar 4, 2026',
    value: 'SP-2026-04521',
  },
];

/**
 * Note: FOR DEMO DATA ONLY
 */
interface FakeRawPatientFact {
  label: string;
  value?: ReactNode;
  redacted?: boolean;
  initiallyExpanded?: boolean;
  relatedBinaryId?: string;
}

/**
 * Note: FOR DEMO DATA ONLY
 */
const FAKE_FACT_GROUPS_RAW: Array<{ label: string; facts: Array<FakeRawPatientFact> }> = [
  {
    label: 'Pastries',
    facts: [
      { label: 'Top choice', value: 'Almond croissant' },
      { label: 'Coffee pairing', value: 'Cappuccino' },
      { label: 'Favorite bakery', value: 'Bouchon Bakery', relatedBinaryId: 'foop' },
      { label: 'Sweet vs. savory', value: 'Sweet', redacted: true },
      { label: 'Frequency', value: '3 times per week', redacted: true },
      { label: 'Favorite filling', value: 'Pastry cream', relatedBinaryId: 'foop', redacted: true },
      { label: 'Pastry they will not eat' },
      { label: 'Glaze preference', value: 'Chocolate' },
      { label: 'Texture preference', value: 'Flaky' },
      { label: 'Warm or room temperature', value: 'Warm' },
    ],
  },
  {
    label: 'Dogs',
    facts: [
      { label: 'Favorite breed', value: 'Golden Retriever' },
      { label: 'Size preference', value: 'Large' },
      { label: 'Coat type', value: 'Long-haired' },
      { label: 'Energy level', value: 'Medium-high', redacted: true },
      { label: 'Trainability importance', value: 'High', relatedBinaryId: 'foop' },
      { label: 'Favorite activity', value: 'Fetch at the beach', relatedBinaryId: 'foop' },
      { label: 'Best dog name encountered', value: 'Biscuit' },
      { label: 'Age of first dog', value: '8 years old' },
      { label: 'Allergic to dogs', value: 'Yes', redacted: true },
      { label: 'Plans for a future dog' },
    ],
  },
  {
    label: 'Coffee preferences',
    facts: [
      { label: 'Roast level', value: 'Medium' },
      { label: 'Brew method', value: 'Pour over' },
      { label: 'Bean origin', value: 'Ethiopian', relatedBinaryId: 'foop' },
      { label: 'Daily cups', value: '2' },
      { label: 'Takes it', value: 'Black', relatedBinaryId: 'foop' },
      { label: 'Decaf in the evening' },
      { label: 'Cold brew acceptable', value: 'Yes' },
      { label: 'Preferred local roastery', value: 'Counter Culture', redacted: true },
      { label: 'Mug material', value: 'Ceramic', redacted: true },
      { label: 'Espresso machine at home', value: 'Breville Bambino' },
    ],
  },
  {
    label: 'Travel',
    facts: [
      { label: 'Most recent trip', value: 'Lisbon, Portugal' },
      { label: 'Dream destination', value: 'New Zealand' },
      { label: 'Mountain or beach', value: 'Mountain', relatedBinaryId: 'foop' },
      { label: 'Solo or group travel', value: 'Group', relatedBinaryId: 'foop' },
      { label: 'Frequent travel companion' },
      { label: 'Next planned trip' },
      { label: 'Passport renewal date', redacted: true },
      { label: 'Carry-on or checked', value: 'Carry-on only', relatedBinaryId: 'foop' },
      { label: 'Countries visited', value: '14' },
      { label: 'Favorite city', value: 'Tokyo' },
      { label: 'Worst travel experience' },
      { label: 'Window or aisle', value: 'Window' },
    ],
  },
  {
    label: 'Hobbies',
    facts: [
      { label: 'Primary hobby', value: 'Skydiving', relatedBinaryId: 'foop' },
      { label: 'Secondary hobby', value: 'Film photography' },
      { label: 'Hours per week', value: '6', redacted: true },
      { label: 'Years practicing primary hobby', value: '4', relatedBinaryId: 'foop' },
      { label: 'Self-rated skill level', value: 'Intermediate', relatedBinaryId: 'foop' },
      { label: 'Monthly hobby budget', value: '$50', relatedBinaryId: 'foop' },
      { label: 'Most recent new hobby', value: 'Sourdough baking' },
      { label: 'Community membership', value: 'Local yoga studio' },
      { label: 'Hobby on hiatus' },
      { label: 'Hobby they want to try next' },
    ],
  },
  {
    label: 'Books',
    facts: [
      { label: 'Last book finished', value: 'The Overstory' },
      { label: 'Favorite genre', value: 'Literary fiction' },
      { label: 'Currently reading', value: 'A Gentleman in Moscow' },
      { label: 'Books per year', value: '22' },
      { label: 'Format preference', value: 'Physical' },
      { label: 'Favorite bookstore', value: 'The Strand' },
      { label: 'Favorite author', value: 'Ursula K. Le Guin' },
      { label: 'Audiobook listener', value: 'On long drives' },
      { label: 'Most re-read book', redacted: true },
      { label: 'Book that made them cry', redacted: true },
    ],
  },
  {
    label: 'Movies',
    facts: [
      { label: 'Recent favorite', value: 'Past Lives' },
      { label: 'Favorite director', value: 'Greta Gerwig' },
      { label: 'Preferred genre', value: 'Drama', relatedBinaryId: 'foop' },
      { label: 'Theater frequency', value: 'Monthly', relatedBinaryId: 'foop' },
      { label: 'Streaming service', value: 'Criterion Channel' },
      { label: 'Snack of choice', value: 'Popcorn, no butter' },
      { label: 'Comfort movie', value: "You've Got Mail", redacted: true },
      { label: 'Movie they have not seen but everyone has' },
      { label: 'Worst movie this year' },
      { label: 'Movie buddy' },
    ],
  },
];

/**
 * Note: FOR DEMO DATA ONLY
 */
const INITIAL_REDACTED_FACT_IDS = new Set<string>();

/**
 * Note: FOR DEMO DATA ONLY
 */
const FAKE_FACT_GROUPS: Array<PatientFactGroup> = FAKE_FACT_GROUPS_RAW.map((group) => ({
  ...group,
  facts: group.facts.map(({ redacted, ...rest }) => {
    const id = crypto.randomUUID();
    if (redacted) {
      INITIAL_REDACTED_FACT_IDS.add(id);
    }
    return { id, ...rest } as PatientFact;
  }),
}));

export const PatientFactExtraction = withErrorBoundary({
  boundaryName: 'PatientFactExtraction',
  Component: () => {
    const [redactedFactIds, setRedactedFactIds] = useState<Set<string>>(
      () => new Set(INITIAL_REDACTED_FACT_IDS),
    );

    const isFactRedacted = (fact: PatientFact) => redactedFactIds.has(fact.id);

    const toggleFactRedaction = ({ fact }: { fact: PatientFact }) => {
      setRedactedFactIds((previous) => {
        const newRedactions = new Set(previous);
        if (newRedactions.has(fact.id)) {
          newRedactions.delete(fact.id);
        } else {
          newRedactions.add(fact.id);
        }
        return newRedactions;
      });
    };

    return (
      <div className={tw`flex flex-col gap-4 px-2 py-1`}>
        <div className={tw`flex flex-row flex-wrap items-center justify-between gap-2`}>
          <SelectInput
            name="specimen"
            label="Specimen:"
            labelPosition="left"
            options={FAKE_SPECIMENS}
            initialValue={FAKE_SPECIMENS[0].value}
            inputClassName={tw`min-w-60 max-w-70`}
            labelClassName={tw`font-semibold text-content-subtle text-sm`}
          />
          <div className={tw`flex shrink-0 flex-row items-center justify-end gap-2`}>
            <div className={tw`whitespace-nowrap font-medium text-content-subtle text-xs`}>
              4/3/2026
            </div>
            <div
              className={tw`whitespace-nowrap rounded-sm bg-background-disabled p-1 font-medium text-content-subtle text-xs`}
            >
              {FAKE_FIELDS_TOTAL} fields in export
            </div>
            <Button
              type="button"
              variant="primary"
              size="xs"
              onClick={() => notify({ type: 'info', title: 'Not implemented yet' })}
              className="py-1.5"
            >
              <div className={tw`flex flex-nowrap items-center space-x-2`}>
                <FontAwesomeIcon icon={faDownload} className={tw`h-4 w-4`} />
                <div className={tw`whitespace-nowrap font-bold text-sm`}>Export</div>
              </div>
            </Button>
          </div>
        </div>
        <div
          className={tw`flex flex-row flex-wrap items-center justify-between gap-3 rounded-md border border-border-main px-4 py-3`}
        >
          <div className={tw`flex flex-row flex-wrap items-center gap-3`}>
            <div className={tw`whitespace-nowrap font-semibold text-content-main text-sm`}>
              {FAKE_FIELDS_EXTRACTED}/{FAKE_FIELDS_TOTAL} fields extracted
            </div>
            <div className={tw`w-32`}>
              <ProgressBar
                label="Fields extracted"
                labelVisuallyHidden={true}
                value={FAKE_FIELDS_EXTRACTED}
                max={FAKE_FIELDS_TOTAL}
                valueText={`${FAKE_FIELDS_EXTRACTED} of ${FAKE_FIELDS_TOTAL} fields extracted`}
              />
            </div>
          </div>
        </div>
        <PatientFactExtractionGroupedTable
          factGroups={FAKE_FACT_GROUPS}
          isRedacted={isFactRedacted}
          onRedactFact={toggleFactRedaction}
          onExpandFact={(_args) => notify({ type: 'info', title: 'Not implemented yet' })}
          onCollapseFact={(_args) => notify({ type: 'info', title: 'Not implemented yet' })}
        />
      </div>
    );
  },
});
